import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiPlay,
  FiPause,
  FiExternalLink,
  FiCheck,
  FiFlag,
  FiRefreshCw,
} from "react-icons/fi";
import { ThemeContext } from "../../App";
import { slugify } from "../../utils/slug";
import "./mock.css";

const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const MIN_COUNT = 1;
const MAX_COUNT = 8;
const MIN_MIN = 15;
const MAX_MIN = 90;

function fmt(secs) {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Background lofi music now lives in a global widget (components/Music/LofiWidget)
// mounted once in App.js, so it keeps playing across every page.

export default function Mock({ questionData, updateData }) {
  const dark = useContext(ThemeContext);

  const [phase, setPhase] = useState("setup"); // setup | run | done
  const [count, setCount] = useState(3);
  const [minutes, setMinutes] = useState(45);
  const [items, setItems] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [running, setRunning] = useState(false);

  // ---- filters (same model as the Practice random-question page) ----
  const [topics, setTopics] = useState([]); // selected topic names ([] = all)
  const [diffs, setDiffs] = useState([]); // selected difficulties ("" = No level)
  const [bookmark, setBookmark] = useState("any"); // any | yes | no
  const [status, setStatus] = useState("unsolved"); // any | unsolved | solved | revise

  const allTopics = useMemo(() => (questionData || []).map((t) => t.topicName), [questionData]);

  function toggleIn(list, setList, value) {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  }

  // pool of problems matching every active filter
  const pool = useMemo(() => {
    const out = [];
    (questionData || []).forEach((t) => {
      if (topics.length && !topics.includes(t.topicName)) return;
      (t.questions || []).forEach((q) => {
        const d = q.Difficulty || "";
        if (diffs.length && !diffs.includes(d)) return;
        if (bookmark === "yes" && !q.Bookmark) return;
        if (bookmark === "no" && q.Bookmark) return;
        if (status === "unsolved" && q.Done) return;
        if (status === "solved" && !q.Done) return;
        if (status === "revise" && !q.Revise) return;
        out.push({ slug: slugify(t.topicName), topic: t.topicName, problem: q.Problem, url: q.URL || "", difficulty: d });
      });
    });
    return out;
  }, [questionData, topics, diffs, bookmark, status]);

  // countdown
  useEffect(() => {
    if (phase !== "run" || !running) return;
    if (secondsLeft <= 0) {
      setRunning(false);
      return;
    }
    const id = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [phase, running, secondsLeft]);

  function start() {
    const chosen = shuffle(pool).slice(0, count);
    if (chosen.length === 0) return;
    setItems(chosen);
    setSecondsLeft(minutes * 60);
    setRunning(true);
    setPhase("run");
  }

  function finish() {
    setRunning(false);
    setPhase("done");
  }

  function reset() {
    setItems([]);
    setRunning(false);
    setSecondsLeft(0);
    setPhase("setup");
  }

  function isSolved(item) {
    const topic = (questionData || []).find((t) => slugify(t.topicName) === item.slug);
    if (!topic) return false;
    const q = (topic.questions || []).find((x) => x.Problem === item.problem);
    return !!(q && q.Done);
  }

  function markSolved(item) {
    const ti = (questionData || []).findIndex((t) => slugify(t.topicName) === item.slug);
    if (ti === -1) return;
    const topic = questionData[ti];
    const pi = (topic.questions || []).findIndex((x) => x.Problem === item.problem);
    if (pi === -1) return;
    const questions = topic.questions.map((q, i) =>
      i === pi ? { ...q, Done: true, SolvedAt: q.SolvedAt || new Date().toISOString() } : q
    );
    const done = questions.filter((q) => q.Done).length;
    updateData(slugify(topic.topicName), { started: done > 0, doneQuestions: done, questions }, ti);
  }

  const timeUp = phase === "run" && secondsLeft <= 0;
  const solvedCount = items.filter(isSolved).length;
  const usedSeconds = minutes * 60 - secondsLeft;

  return (
    <div className={`mock-page ${dark ? "mock-dark" : ""}`}>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="stopwatch">
          ⏱️
        </div>
        <h1 className="page-title">Mock interview</h1>
        <p className="page-sub">
          Simulate the pressure: pick a set, start the clock, and solve against time — with optional
          lofi in the background to stay in flow.
        </p>
      </div>

      {phase === "setup" ? (
        <div className="mk-setup">
          <div className="mk-field">
            <div className="mk-field-row">
              <span className="mk-field-lbl">Problems</span>
              <span className="mk-field-val">{count}</span>
            </div>
            <input type="range" className="mk-slider" min={MIN_COUNT} max={MAX_COUNT} value={count} onChange={(e) => setCount(Number(e.target.value))} />
          </div>
          <div className="mk-field">
            <div className="mk-field-row">
              <span className="mk-field-lbl">Time limit</span>
              <span className="mk-field-val">{minutes} min</span>
            </div>
            <input type="range" className="mk-slider" min={MIN_MIN} max={MAX_MIN} step={5} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} />
          </div>
          <div className="mk-field">
            <span className="mk-field-lbl">Filters</span>
            <div className="pr-filters mk-filters">
              <div className="pr-frow">
                <div className="pr-flabel">
                  Topics
                  {topics.length ? <span className="pr-fcount">{topics.length}</span> : <span className="pr-fall">All</span>}
                </div>
                <div className="pr-chips">
                  {allTopics.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className={`pr-chip ${topics.includes(name) ? "active" : ""}`}
                      onClick={() => toggleIn(topics, setTopics, name)}
                    >
                      {name}
                    </button>
                  ))}
                  {topics.length ? (
                    <button type="button" className="pr-chip clear" onClick={() => setTopics([])}>
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="pr-frow">
                <div className="pr-flabel">
                  Difficulty
                  {diffs.length ? <span className="pr-fcount">{diffs.length}</span> : <span className="pr-fall">All</span>}
                </div>
                <div className="pr-chips">
                  {DIFFICULTIES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`pr-chip ${diffs.includes(d) ? "active" : ""}`}
                      onClick={() => toggleIn(diffs, setDiffs, d)}
                    >
                      {d}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`pr-chip ${diffs.includes("") ? "active" : ""}`}
                    onClick={() => toggleIn(diffs, setDiffs, "")}
                  >
                    No level
                  </button>
                </div>
              </div>

              <div className="pr-frow">
                <div className="pr-flabel">Bookmark</div>
                <div className="pr-seg">
                  {[
                    ["any", "Any"],
                    ["yes", "Bookmarked"],
                    ["no", "Not bookmarked"],
                  ].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      className={`pr-segbtn ${bookmark === val ? "active" : ""}`}
                      onClick={() => setBookmark(val)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pr-frow">
                <div className="pr-flabel">Status</div>
                <div className="pr-seg">
                  {[
                    ["any", "Any"],
                    ["unsolved", "Unsolved"],
                    ["solved", "Solved"],
                    ["revise", "To revise"],
                  ].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      className={`pr-segbtn ${status === val ? "active" : ""}`}
                      onClick={() => setStatus(val)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mk-start-row">
            <button type="button" className="nb-btn nb-btn-primary" onClick={start} disabled={pool.length === 0}>
              <FiPlay size={15} /> Start session
            </button>
            <span className="mk-pool">
              {pool.length === 0 ? "No problems match these filters" : `${Math.min(count, pool.length)} of ${pool.length} available`}
            </span>
          </div>
        </div>
      ) : null}

      {phase === "run" ? (
        <div className="mk-run">
          <div className="mk-run-main">
            <div className={`mk-timer ${timeUp ? "up" : ""}`}>
              <span className="mk-time">{fmt(secondsLeft)}</span>
              <div className="mk-timer-actions">
                <button type="button" className="nb-btn nb-btn-sm" onClick={() => setRunning((r) => !r)} disabled={timeUp}>
                  {running ? <FiPause size={14} /> : <FiPlay size={14} />} {running ? "Pause" : "Resume"}
                </button>
                <button type="button" className="nb-btn nb-btn-sm" onClick={finish}>
                  <FiFlag size={14} /> Finish
                </button>
              </div>
              {timeUp ? <div className="mk-up-note">Time’s up — wrap up and finish.</div> : null}
            </div>

            <ul className="mk-list">
              {items.map((item, i) => {
                const solved = isSolved(item);
                return (
                  <li key={`${item.slug}-${i}`} className={`mk-item ${solved ? "solved" : ""}`}>
                    <span className="mk-item-num">{solved ? <FiCheck size={14} /> : i + 1}</span>
                    <div className="mk-item-main">
                      <span className="mk-item-name" title={item.problem}>{item.problem}</span>
                      <span className="mk-item-meta">
                        {item.topic}{item.difficulty ? ` · ${item.difficulty}` : ""}
                      </span>
                    </div>
                    {item.url ? (
                      <a className="nb-btn nb-btn-sm" href={item.url} target="_blank" rel="noopener noreferrer">
                        <FiExternalLink size={13} /> Open
                      </a>
                    ) : null}
                    <button type="button" className="nb-btn nb-btn-sm" onClick={() => markSolved(item)} disabled={solved}>
                      {solved ? "Solved" : "Mark solved"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}

      {phase === "done" ? (
        <div className="mk-done">
          <div className="mk-done-emoji">🏁</div>
          <div className="mk-done-stats">
            <div className="mk-done-stat">
              <span className="mk-done-num">{solvedCount}/{items.length}</span>
              <span className="mk-done-lbl">solved</span>
            </div>
            <div className="mk-done-stat">
              <span className="mk-done-num">{fmt(Math.max(0, usedSeconds))}</span>
              <span className="mk-done-lbl">time used</span>
            </div>
          </div>
          <ul className="mk-done-list">
            {items.map((item, i) => (
              <li key={`${item.slug}-${i}`} className={`mk-done-row ${isSolved(item) ? "solved" : ""}`}>
                <span className="mk-done-check">{isSolved(item) ? <FiCheck size={13} /> : "·"}</span>
                <span className="mk-done-name" title={item.problem}>{item.problem}</span>
                <span className="mk-done-topic">{item.topic}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="nb-btn nb-btn-primary" onClick={reset}>
            <FiRefreshCw size={15} /> New session
          </button>
        </div>
      ) : null}
    </div>
  );
}
